import { z } from 'zod'

import { PlatformSchema } from './platform'

/**
 * What a push channel actually looks like from OneSignal, per app and per
 * platform (push-readiness-platform-split plan U1, R4 / R5 / R9).
 *
 * READINESS CANNOT ASK ONESIGNAL. `resolve-app-readiness.ts` is pure and runs on
 * every read of the fleet page and every app detail panel, so the facts a
 * `push-ios` / `push-android` verdict needs — is there an APNs key, is there an
 * Android transport — arrive from a snapshot a scheduled sweep wrote, not from a
 * live call. THIS FILE is the contract between those two halves: the sweep
 * writes exactly this and readiness reads exactly this, so neither can grow a
 * field the other does not know about.
 *
 * NO KEY MATERIAL, EVER (R5). Every credential here is a boolean or a non-secret
 * identifier, which is the same line `mapApp` in
 * `apps/backend/src/interfaces/one-signal/provisioning.ts` already draws at the
 * HTTP boundary: the raw APNs `.p8`, the FCM service-account JSON and the REST
 * key value never leave that module and must never reach this one. A schema that
 * has nowhere to put a secret cannot accidentally persist or log one.
 */

/**
 * The outcome of the LAST attempt to observe a fact group, whether that attempt
 * succeeded or not.
 *
 *   observed     the probe ran and the facts beside it are what it saw
 *   unreadable   the probe ran and failed; `checkError` says how (scrubbed).
 *                The facts beside it are the PREVIOUS reading, left in place
 *                (R7) — a failed check never clears a finding
 *   stale_check  the stored column default. The row exists and nothing has ever
 *                read a provider for this group. NEVER a reading
 *
 * SPELLED THE SAME AS {@link DeliveryCheckStatusSchema}, deliberately and to the
 * token. This repo already spells this one concept three ways —
 * {@link CredentialCheckStatusSchema} and
 * {@link ObservedStandingCheckStatusSchema} carry the two-value form, the
 * delivery ledger carries this three-value one — and a fourth spelling here
 * (`cannot_check`, say) would be the drift the split exists to end. This is a
 * separate enum rather than a re-export because the two are free to diverge in
 * MEANING later; they are not free to diverge in vocabulary now.
 *
 * `unreadable` is the SCH-1192 contract: an unreadable snapshot resolves the
 * capability to `blocked` with reason `unreadable`, which surfaces render as
 * "not checked" and never as a credential to go upload (R8).
 */
export const PUSH_CHANNEL_CHECK_STATUSES = ['observed', 'unreadable', 'stale_check'] as const
export const PushChannelCheckStatusSchema = z.enum(PUSH_CHANNEL_CHECK_STATUSES)
export type PushChannelCheckStatus = z.infer<typeof PushChannelCheckStatusSchema>

/**
 * Per-platform successful/failed/errored delivery counts over the last N
 * notifications the app sent (R9, Q2 — N is 20 today, matching `DELIVERY_WINDOW`
 * in `tooling/sj-cli/src/commands/onesignal.ts`).
 *
 * ⚠️ A COUNTER OF ZERO IS NOT EVIDENCE OF A BROKEN CHANNEL (KTD6, R10). Zero
 * successful Android deliveries is equally consistent with a dead FCM
 * credential and with an app that simply has no Android subscribers, and
 * nothing in this snapshot can tell those apart — 22 Miami Country Day is the
 * live case: FCM present, 71 successful iOS, 0 successful Android. So these
 * numbers are ADVISORY and must never move a capability's status. A reader that
 * turns a zero into `push-android: blocked` puts an item on an operator's list
 * that no operator can fix. Render it as its own advisory state, beside the
 * status, never as the status.
 *
 * ITS OWN CHECK STATUS AND ITS OWN TIMESTAMP, separate from the channel group's,
 * because the two lanes fail independently (KTD5): the fleet's channel presence
 * comes from ONE `GET /apps` with the organization key, while delivery evidence
 * needs each app's own `onesignal_api_key` (the org key 401s on
 * `GET /notifications`). For an app whose REST key has not been minted yet, the
 * channel lane succeeds and this one is structurally unreadable — and the row
 * has to be able to say exactly that rather than reporting zero deliveries
 * (R11).
 *
 * These field names are the `delivery`-prefixed ones on purpose: the state row
 * below spreads this shape rather than restating it, so the probe's output and
 * the stored projection cannot drift into two vocabularies.
 */
export const PushChannelDeliveryEvidenceSchema = z.object({
  /** Null when this lane has never produced a reading — not zero. */
  deliverySuccessful: z.number().int().nonnegative().nullable(),
  deliveryFailed: z.number().int().nonnegative().nullable(),
  deliveryErrored: z.number().int().nonnegative().nullable(),
  /**
   * How many notifications the counts above were drawn from. It is the
   * denominator that makes them readable: 0 successful out of 0 inspected is
   * "this app has never sent anything", which is not the same finding as 0 out
   * of 20.
   */
  inspectedNotifications: z.number().int().nonnegative().nullable(),
  deliveryCheckStatus: PushChannelCheckStatusSchema,
  /**
   * Scrubbed BY THE WRITER, never raw provider text, which can echo a key back.
   *
   * The cap is the enforcement this schema can honestly offer: scrubbing is a
   * judgement about content that belongs to the sweep, and a pattern guard here
   * would either reject legitimate diagnostics or give false confidence about
   * the ones it did not match. What the cap does buy is that the two shapes a
   * leak actually takes — a service-account JSON blob, a base64 `.p8` — cannot
   * fit through it.
   */
  deliveryCheckError: z.string().max(500).nullable(),
  /** ISO-8601, and only a SUCCESSFUL read moves it. Null until one has. */
  deliveryObservedAt: z.string().datetime().nullable()
})
export type PushChannelDeliveryEvidence = z.infer<typeof PushChannelDeliveryEvidenceSchema>

/**
 * The channel half of the snapshot: what OneSignal reports about the credential
 * this platform needs, plus its own provenance.
 *
 * ONE `credentialPresent`, NOT `hasApnsKey` AND `hasFcmServiceAccount`. The
 * `platform` field already says which credential is meant, so a per-platform
 * boolean pair would leave one of the two always null and invite a reader to
 * check the wrong one. The identifier columns are shared the same way — on an
 * iOS row `credentialKeyId` / `credentialTeamId` / `credentialBundleId` /
 * `credentialEnv` hold the APNs values and `credentialSenderId` /
 * `credentialPackage` are null; on an Android row it is the other way round.
 */
export const PushChannelCredentialFactsSchema = z.object({
  /**
   * APNs key on an iOS row, an Android transport on an Android row.
   *
   * Null means UNKNOWN, not absent: a row whose `channelCheckStatus` is
   * `stale_check` or `unreadable` and that has never been observed has nothing
   * to report, and reporting `false` there would tell an operator to go upload a
   * credential we simply have not looked for.
   */
  credentialPresent: z.boolean().nullable(),
  /**
   * ANDROID ONLY, and always null on the iOS row: this app's Android transport
   * is a legacy `gcm_key` rather than an FCM v1 service account (KTD9).
   *
   * Legacy GCM STILL DELIVERS, so this must not read as blocked — an app here is
   * Android-on-a-retired-transport, not Android-dead. It is a distinct fact from
   * `credentialPresent` (which is true in this case) because the transport is
   * retired and the app needs migrating even while it works.
   */
  credentialLegacy: z.boolean().nullable(),
  /** APNs key id (iOS). */
  credentialKeyId: z.string().nullable(),
  /** APNs team id (iOS). */
  credentialTeamId: z.string().nullable(),
  /** APNs bundle id (iOS). */
  credentialBundleId: z.string().nullable(),
  /** APNs environment, `production` or `sandbox` as OneSignal spells it (iOS). */
  credentialEnv: z.string().nullable(),
  /** FCM sender id (Android). */
  credentialSenderId: z.string().nullable(),
  /** Android package name. */
  credentialPackage: z.string().nullable(),
  channelCheckStatus: PushChannelCheckStatusSchema,
  /** Scrubbed by the writer and capped, for the reason the delivery lane's twin gives. */
  channelCheckError: z.string().max(500).nullable(),
  /** ISO-8601, and only a SUCCESSFUL read moves it. Null until one has. */
  channelObservedAt: z.string().datetime().nullable()
})
export type PushChannelCredentialFacts = z.infer<typeof PushChannelCredentialFactsSchema>

/**
 * ONE snapshot row: one app on one platform, both fact groups and both
 * provenances.
 *
 * WHY THE TWO GROUPS ARE SPREAD FLAT rather than nested under `channel` and
 * `delivery`: this shape is the projection of a single
 * `MobileAppPushChannelState` row, whose columns are flat and uniquely
 * `@@unique([mobileAppId, platform])`, and a nesting the database does not have
 * is one more mapping for the sweep and readiness to disagree about.
 *
 * FRESHNESS IS NOT STORED. Whether `channelObservedAt` is stale enough to force
 * `blocked`/`unreadable` is decided at read time against a configured threshold
 * (24 hours against a 6-hour sweep, so three consecutive sweep failures are
 * needed before a row goes stale). A stored `isStale` boolean would be a lie the
 * moment the clock moved past it.
 */
export const PushChannelStateSchema = z
  .object({
    /** `MobileApp.publicId` — the app this snapshot describes. */
    mobileAppPublicId: z.string(),
    platform: PlatformSchema,
    /**
     * The OneSignal app id the facts were read from, so a snapshot can be told
     * apart from one taken before the registry was repointed.
     */
    oneSignalAppId: z.string().nullable(),
    /**
     * APP-LEVEL, NOT PER-PLATFORM. OneSignal reports subscription counts for the
     * app as a whole, so BOTH platform rows carry the same value — do not read
     * `messageablePlayers` on the Android row as an Android subscriber count,
     * which is the very number that would make the zero-delivery advisory above
     * look decidable.
     */
    players: z.number().int().nonnegative().nullable(),
    messageablePlayers: z.number().int().nonnegative().nullable(),
    /** ISO-8601. When the snapshot row itself was last written. */
    updatedAt: z.string().datetime().nullable()
  })
  .extend(PushChannelCredentialFactsSchema.shape)
  .extend(PushChannelDeliveryEvidenceSchema.shape)
  // The identifier columns are shared between the two platforms, so only this
  // check makes the docblock above enforceable: an APNs key id on an Android row
  // is a writer that mapped the wrong half of `OneSignalApp`, and it would be
  // read back as a real identifier by a panel that has no reason to doubt it.
  .refine(
    row =>
      row.platform !== 'IOS' ||
      (row.credentialSenderId === null && row.credentialPackage === null && row.credentialLegacy === null),
    { message: 'an IOS row carries APNs identifiers only; the Android fields must be null' }
  )
  .refine(
    row =>
      row.platform !== 'ANDROID' ||
      (row.credentialKeyId === null &&
        row.credentialTeamId === null &&
        row.credentialBundleId === null &&
        row.credentialEnv === null),
    { message: 'an ANDROID row carries Android identifiers only; the APNs fields must be null' }
  )
export type PushChannelState = z.infer<typeof PushChannelStateSchema>

/**
 * Both platforms' snapshots as they hang off an app.
 *
 * Either side is ABSENT until the sweep has created that row — the registry does
 * not pre-seed them — and absent resolves exactly like `stale_check` does: not
 * checked, never ready (R8).
 *
 * THE KEY AND THE ROW'S OWN `platform` MUST AGREE, and the refinement below is
 * what makes that a parse error rather than a silent one. Both rows are the same
 * shape, so nothing else stops an Android snapshot sitting under `ios`, and a
 * reader that keys by platform — which is every reader, since that is the whole
 * point of the split — would then answer the iOS question with Android facts.
 */
export const MobileAppPushChannelSchema = z
  .object({
    ios: PushChannelStateSchema.optional(),
    android: PushChannelStateSchema.optional()
  })
  .refine(value => value.ios === undefined || value.ios.platform === 'IOS', {
    path: ['ios'],
    message: 'the snapshot under `ios` must carry platform IOS'
  })
  .refine(value => value.android === undefined || value.android.platform === 'ANDROID', {
    path: ['android'],
    message: 'the snapshot under `android` must carry platform ANDROID'
  })
export type MobileAppPushChannel = z.infer<typeof MobileAppPushChannelSchema>
